import type { AxiosResponse } from "axios";
import Axios from "axios";
import type { MutableRefObject, ChangeEvent } from "react";
import { useLayoutEffect, ReactNode } from "react";
import type { DeepMap, FieldError, UseFormMethods } from "react-hook-form";
import { Controller, FormProvider } from "react-hook-form";
import type { TFunction } from "react-i18next";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { z } from "zod";
import type { ChipType } from "../../../../../../components/Chips/Chips";
import { DatePicker } from "../../../../../../components/DatePicker/DatePicker";
import { CheckIcon, XIcon } from "../../../../../../components/Icons/Icons";
import { useNotifications } from "../../../../../../components/Notifications/NotificationsContext";
import { RichEditor } from "../../../../../../components/RichEditor/RichEditor";
import { SelectBoxV2 } from "../../../../../../components/SelectBoxV2/SelectBoxV2";
import { TextField } from "../../../../../../components/TextFields/TextFields";
import { ToggleSwitchV2 } from "../../../../../../components/ToggleSwitch/ToggleSwitch";
import { endpoints } from "../../../../../../endpoints";
import {
  User,
  MethodsOfUseForm,
  OptionType,
  UUID,
} from "../../../../../../types/types";
import type {
  AttributeSchema,
  AttributeValue,
  DocumentAttributeValue,
  ImageAttributeValue,
  IproductReferenceCollectionItem,
  ObjectReferenceResult,
  PIMProductPartial,
} from "../../../../../../types/types.PIM";
import type { DocumentCollectionValue } from "../../../../../../util/collectionAttributeDisplayValue";
import { strings } from "../../../../../../util/strings";
import { shouldUseLazyListChoices } from "../../../../../../util/attributeChoices";
import { CollectionSingleSelectListBackedField } from "../../../../../../components/AttributeGroupFormGenerator/ListBackedSelectField";
import { convertUserToOption, toTitleCase } from "../../../../../../util/util";
import {
  LinkAttributeValueSchema,
  LinkAttributeValueSchemaRefined,
  zodMultiSelectDefault,
  zodOptionalSelectBoxDefault,
  zodOptionalString,
  zodRequiredString,
} from "../../../../../../util/zod.util";
import {
  getCellName,
  buildSkuAttributeValueLookup,
  resolveSkuStatusLock,
} from "./approvalWorkflowHelpers";
import type { ApprovalConfigurationUIRules } from "../../../../../../types/types.Workflow";
import type {
  FormValue,
  IRow,
  OnSubmitValue,
  MultiSelectRefType,
} from "./type";
import { ActionTypes, InputActionMeta } from "react-select";
import {
  DocumentAttribute,
  MultiSelectField,
  ProductReferenceField,
  UserReferenceField,
} from "../../../../../../components/AttributeGroupFormGenerator/AttributeGroupFormGenerator";
import { LinkAttributeField } from "../../../../../../components/LinkAttributeField/LinkAttributeField";
import {
  getFilteredChoices,
  shouldSkipAutofillForReferenceEntityConstraint,
} from "./allUnlessRestricted";
import { ObjectReferenceField } from "../../../../../../components/ObjectReference/ObjectReferenceField";
import {
  AddressAttributeForm,
  AddressAttributeFormProps,
} from "../../SellerAdminPIMAttributes/AddressAttributes/AddressAttributeForm";
import { AddressValue } from "../../SellerAdminPIMAttributes/AddressAttributes/address.utils";
export type { MultiSelectRefType } from "./type";

const DateWrapper = styled.div``;

export const VerticalLinkTextFieldsContainer = styled.div`
  display: flex;
  flex-direction: column;
  div.text_errorMessageWrapper {
    position: unset;
  }
`;

export const GENERATED_UUID = "-generated";

export const getAttributeValue = (
  attr: AttributeValue | undefined,
  row: IRow,
  is_sku_source: boolean = false
):
  | string
  | boolean
  | null
  | undefined
  | {
      name: string;
      status?: AttributeValue["status"];
      is_sku_source?: boolean;
    }[]
  | { display_text: string; url: string }
  | ImageAttributeValue
  | DocumentAttributeValue
  | DocumentCollectionValue[]
  | IproductReferenceCollectionItem
  | AddressValue
  | User => {
  if (attr?.data_type === "document") {
    const { asset_name, asset_type, content_type, host } = attr;
    const docValue = attr.value as DocumentAttributeValue;
    const prev: DocumentCollectionValue[] = row[attr.attribute_id] ?? [];
    prev.push({
      ...docValue,
      display_name: asset_name,
      asset_type,
      content_type,
      host_name: host?.name,
      is_external: !!host && host.name !== "Agilis",
      url: attr.cdn_url ?? attr.signed_url ?? attr.preview_url,
    });
    return prev;
  } else if (attr?.data_type === "enum") {
    const prev = row[attr?.attribute_id] ?? [];
    prev.push({
      name: attr.enum_variant?.variant ?? "",
      status: attr?.status ?? null,
      badge_color: attr.enum_variant?.badge_color ?? undefined,
      is_sku_source,
    });
    return prev;
  } else if (attr?.product_ref?.id) {
    const prev = row[attr?.attribute_id] ?? [];
    prev.push({
      name: attr?.product_ref?.name,
      value: attr?.product_ref?.id,
      slug: attr?.product_ref?.slug,
      is_visible_on_storefronts: attr?.product_ref?.is_visible_on_storefronts,
      visibility_tenants: attr?.product_ref?.visibility_tenants,
      is_accessible: attr?.product_ref?.is_accessible,
      is_viewable: attr?.product_ref?.is_viewable,
      pim_product_type: attr?.product_ref?.pim_product_type,
      status: attr?.product_ref?.status,
      id: attr?.product_ref.id,
      brand_name: attr?.product_ref?.brand_name,
      chemical_name: attr?.product_ref?.chemical_name,
      cas_number: attr?.product_ref?.cas_number,
      product_description: attr?.product_ref?.product_description,
      image_url: attr?.product_ref?.image_url,
      product_number: attr?.product_ref?.product_number,
    });
    return prev;
  } else if (attr?.user) {
    const prev = row[attr?.attribute_id] ?? [];
    prev.push(attr?.user);
    return prev;
  } else if (attr?.object_ref?.id) {
    const prev = Array.isArray(row[attr?.attribute_id])
      ? row[attr.attribute_id]
      : [];
    prev.push({
      id: attr.object_ref.id,
      name: attr.object_ref.name,
      object_type: attr.object_ref.object_type,
      object_number: attr.object_ref.object_number,
      status: attr.object_ref.status,
      slug: attr.object_ref.slug,
      image_url: attr.object_ref.image_url,
      preview_attributes: attr.object_ref.preview_attributes ?? [],
      is_visible_on_storefronts: attr.object_ref.is_visible_on_storefronts,
      is_accessible: attr.object_ref.is_accessible,
      is_viewable: attr.object_ref.is_viewable,
      pim_product_type: attr.object_ref.pim_product_type,
      visibility_tenants: attr.object_ref.visibility_tenants ?? [],
    });
    return prev;
  }
  return attr?.value ?? "";
};

const normalizeObjectReferenceValues = (
  rawValue: unknown,
  attributeId: UUID,
  targetObjectType?: string
): AttributeValue[] => {
  const values = Array.isArray(rawValue)
    ? rawValue
    : rawValue != null
    ? [rawValue]
    : [];

  return values.flatMap((item, index) => {
    if (!item || typeof item !== "object") {
      return [];
    }

    const objAttributeValue = item as AttributeValue;
    if (objAttributeValue.object_ref?.id) {
      return [objAttributeValue];
    }

    const objectRef = item as Partial<ObjectReferenceResult>;
    if (
      typeof objectRef.id !== "string" ||
      typeof objectRef.name !== "string"
    ) {
      return [];
    }

    return [
      {
        id: objectRef.id as UUID,
        priority: index,
        enum_variant: null,
        attribute_id: attributeId,
        value: objectRef.name,
        data_type: "text",
        object_ref: {
          id: objectRef.id as UUID,
          name: objectRef.name,
          object_type: objectRef.object_type ?? targetObjectType ?? "product",
          object_number: objectRef.object_number ?? null,
          status: objectRef.status ?? null,
          slug: objectRef.slug ?? null,
          image_url: objectRef.image_url ?? null,
          preview_attributes: objectRef.preview_attributes ?? [],
          pim_product_type: objectRef.pim_product_type,
        },
      } as AttributeValue,
    ];
  });
};

export const getValidationForProductCollection = ({
  input_type,
  is_multiple_choice,
  optional,
  t,
}: {
  input_type: AttributeSchema["input_type"];
  is_multiple_choice: boolean;
  optional: boolean;
  t: TFunction;
}) => {
  switch (input_type) {
    case "single_select":
      return optional
        ? zodOptionalSelectBoxDefault.nullable()
        : z.any().refine(
            (val) => {
              return Boolean(val?.value) && Boolean(val?.label);
            },
            { message: strings(t).thisIsARequiredField }
          );
    case "numeric":
      return optional
        ? z.union([z.number(), z.string()]).optional().nullable()
        : z
            .union([z.number(), z.string()])
            .refine((val) => val !== "" && !Number.isNaN(Number(val)), {
              message: strings(t).thisIsARequiredField,
            });
    case "user":
    case "product_reference":
    case "object_reference":
      return optional
        ? is_multiple_choice
          ? zodOptionalSelectBoxDefault.array().optional().nullable()
          : zodOptionalSelectBoxDefault.optional().nullable()
        : is_multiple_choice
        ? z.any().refine(
            (val) => {
              if (Array.isArray(val) && val.length > 0) {
                return val.every(
                  (item) => Boolean(item.value) && Boolean(item.label)
                );
              }
              return false;
            },
            { message: strings(t).thisIsARequiredField }
          )
        : z.any().refine(
            (val) => {
              return Boolean(val?.value) && Boolean(val?.label);
            },
            { message: strings(t).thisIsARequiredField }
          );
    case "multi_select":
      return optional
        ? z
            .union([z.string(), z.object({ name: zodOptionalString })])
            .array()
            .optional()
            .nullable()
        : z
            .union([z.string(), zodMultiSelectDefault(t)])
            .array()
            .min(1, strings(t).thisIsARequiredField)
            .refine((val) => (val ?? []).length > 0, {
              message: strings(t).thisIsARequiredField,
            });
    case "multiline_entry":
      return optional ? zodOptionalString : zodRequiredString(t);
    case "link":
      return optional
        ? LinkAttributeValueSchemaRefined(t).nullable()
        : LinkAttributeValueSchema.refine(
            (val) => {
              return Boolean(val?.url) && Boolean(val?.display_text);
            },
            {
              message: t("Display Text and URL are required"),
              path: ["display_text"],
            }
          );
    case "toggle":
    case "checkbox":
      // for some weird reasons, sometimes react-form library turns boolean to
      // ['on'] for true and [] for false
      // this fixes that.
      const zBool = z.preprocess((value) => {
        if (Array.isArray(value)) {
          return value.length > 0;
        }
        return value;
      }, z.any());
      return optional ? zBool.optional() : zBool;

    case "address":
      return z.any().optional().nullable();

    case "document":
      return optional
        ? z.array(z.any()).optional().nullable()
        : z.array(z.any()).min(1, strings(t).thisIsARequiredField);

    default:
      return optional ? zodOptionalString : zodRequiredString(t);
  }
};

const FormFieldInitializer = ({
  col,
  value,
  methodsOfUseForm,
  children,
}: {
  col: AttributeSchema;
  value: any;
  row: IRow;
  methodsOfUseForm: MethodsOfUseForm;
  children: ReactNode;
}) => {
  const { setValue } = methodsOfUseForm;

  useLayoutEffect(() => {
    if (
      col.input_type === "product_reference" ||
      col.input_type === "object_reference"
    ) {
      const isMultipleRefs =
        Array.isArray(value) && (value as any[])?.length > 1;
      if (!isMultipleRefs) {
        setValue(
          col.id,
          {
            label: (value as { name: string; id: string }[])?.[0]?.name ?? "",
            value: (value as { name: string; id: string }[])?.[0]?.id ?? "",
          },
          { shouldValidate: false }
        );
      }
    }
  }, [col.id, col.input_type, value, setValue]);

  return <>{children}</>;
};

/** Enforce locked attribute value from ui_rules outside render to avoid loops. */
const EnforceLockedSkuStatusValue = ({
  shouldEnforce,
  colId,
  enforcedValue,
  methodsOfUseForm,
}: {
  shouldEnforce: boolean;
  colId: string;
  enforcedValue: string;
  methodsOfUseForm: MethodsOfUseForm;
}) => {
  const { setValue, getValues, watch } = methodsOfUseForm;
  const watchedValue = watch(colId);

  useLayoutEffect(() => {
    if (!shouldEnforce || !enforcedValue) {
      return;
    }
    const current = (watchedValue ?? getValues(colId)) as
      | OptionType<string>
      | string
      | undefined;
    const currentValue =
      typeof current === "object" && current !== null ? current.value : current;
    if (currentValue === enforcedValue) {
      return;
    }
    setValue(
      colId,
      { label: enforcedValue, value: enforcedValue },
      { shouldValidate: false, shouldDirty: true }
    );
  }, [shouldEnforce, colId, enforcedValue, watchedValue, getValues, setValue]);

  return null;
};

export const ApplyFilteredChoiceValue = ({
  colId,
  inputType,
  nextValue,
  setValue,
  multiSelectRefs,
}: {
  colId: string;
  inputType: "single_select" | "multi_select";
  nextValue: { type: "clear" | "autofill"; value: any } | null;
  setValue: (
    name: string,
    value: any,
    options?: { shouldValidate?: boolean }
  ) => void;
  multiSelectRefs: React.MutableRefObject<{
    [key: string]: MultiSelectRefType;
  }>;
}) => {
  useLayoutEffect(() => {
    if (!nextValue) {
      return;
    }
    setValue(colId, nextValue.value, { shouldValidate: true });
    if (inputType === "multi_select") {
      const chips =
        nextValue.type === "clear"
          ? []
          : (nextValue.value as { name: string }[]) ?? [];
      multiSelectRefs.current[colId]?.setChips(chips);
    }
  }, [colId, inputType, multiSelectRefs, nextValue, setValue]);

  return null;
};

export const createFormFieldForCollection = ({
  col,
  value,
  row,
  methodsOfUseForm,
  errors,
  t,
  genericConfig,
  has_reference_entity_mapping,
  updateDependentAttributes,
  dependent_attributes_constraint,
  allRows,
  allColumns,
  collectionSchemaName,
  multiSelectRefs,
  clearableAttributes,
  all_dependent_constraints,
  menuPortalTarget,
  menuPortalZIndex,
  excludeProfileId,
  tenant_id,
}: {
  col: AttributeSchema;
  value:
    | string
    | null
    | boolean
    | number
    | { name: string }[]
    | { display_text: string; url: string }
    | User[];
  row: IRow;
  methodsOfUseForm: MethodsOfUseForm;
  errors: DeepMap<FormValue, FieldError>;
  tenant_id?: string;
  storefront_id?: string;
  t: TFunction;
  genericConfig?: {
    isBulkPackaging?: boolean;
    packagingTypeColId?: string;
    genericSkuPackagingTypeName?: string;
    sku_source_col_id?: string;
    columns?: AttributeSchema[];
    enable_generic_sku?: boolean;
    uiRules?: ApprovalConfigurationUIRules[];
  };
  has_reference_entity_mapping: boolean;
  updateDependentAttributes?: (params: {
    attributeId?: string;
    clearSelection?: boolean;
  }) => void;
  dependent_attributes_constraint?: {
    action: string;
    valid_values:
      | {
          display: string;
        }[]
      | undefined;
  };
  allRows?: IRow[];
  allColumns?: AttributeSchema[];
  collectionSchemaName?: string;
  multiSelectRefs: React.MutableRefObject<{
    [key: string]: MultiSelectRefType;
  }>;
  useConstraintsValue?: boolean;
  clearableAttributes?: Set<string>;
  all_dependent_constraints?: { attribute_id: string; action: string }[];
  menuPortalTarget?: HTMLElement | null;
  menuPortalZIndex?: number;
  excludeProfileId?: string;
}) => {
  const dropdownZ = menuPortalZIndex ?? 600;
  const { formState, register, setValue, control, watch, clearErrors } =
    methodsOfUseForm;
  const hasValue = Array.isArray(value) ? value.length > 0 : Boolean(value);
  let is_single_select_disabled = false;

  const { packagingTypeColId, genericSkuPackagingTypeName, sku_source_col_id } =
    genericConfig || {};
  const watchedPackagingType = packagingTypeColId
    ? watch(packagingTypeColId)
    : null;
  const currentPackagingType =
    typeof watchedPackagingType === "object" && watchedPackagingType?.value
      ? watchedPackagingType.value
      : watchedPackagingType;

  let shouldEnforceLockedSkuStatus = false;
  let lockSkuStatusValue: string | undefined;

  if (col.object_type === "sku_status") {
    const attributeValues = buildSkuAttributeValueLookup({
      columns: genericConfig?.columns,
      row: row as Record<string, unknown>,
      getFormValue: (columnId) => watch(columnId),
    });

    // Region chip may be driven by a dedicated sku_source form field.
    if (sku_source_col_id) {
      const currentRegion = watch(sku_source_col_id) as
        | OptionType<string>
        | undefined;
      if (currentRegion) {
        attributeValues.region = getCellName(currentRegion);
      }
    }

    const resolved = resolveSkuStatusLock({
      collectionName: collectionSchemaName,
      attributeValues,
      uiRules: genericConfig?.uiRules,
    });
    is_single_select_disabled = resolved.locked;
    lockSkuStatusValue = resolved.value;
    // Disable for any locked match; only force-write the lock value on new/cloned rows
    // so existing Rejected/Approved statuses are not overwritten when editing.
    const rowKey = String(row.uuid ?? row.id ?? "");
    const isNewOrClonedRow =
      !!row.edit && (!rowKey || rowKey.includes(GENERATED_UUID));
    shouldEnforceLockedSkuStatus =
      resolved.locked && !!lockSkuStatusValue && isNewOrClonedRow;
  }

  // Aggregate all defaultValue logic into a single IIFE
  const defaultValue = (() => {
    switch (col.input_type) {
      case "single_select": {
        const arrayValue = value as { name: string }[] | undefined;
        return {
          label: arrayValue?.[0]?.name,
          value: arrayValue?.[0]?.name,
        };
      }

      case "multi_select": {
        return value
          ? (value as { name: string }[] | string[])?.map((v) =>
              typeof v === "string" ? v : v?.name
            )
          : [];
      }

      case "product_reference": {
        const arrayValue = value as { name: string; id: string }[] | undefined;
        return col.is_multiple_choice
          ? arrayValue
          : {
              label: arrayValue?.[0]?.name ?? "",
              value: arrayValue?.[0]?.id ?? "",
            };
      }
      case "object_reference": {
        const arrayValue = value as ObjectReferenceResult[] | undefined;
        return col.is_multiple_choice
          ? arrayValue?.map((item) => ({ label: item.name, value: item.id })) ??
              []
          : {
              label: arrayValue?.[0]?.name ?? "",
              value: arrayValue?.[0]?.id ?? "",
            };
      }

      case "user": {
        const userValue = (value as User[]) || [];
        if (col.is_multiple_choice) {
          return userValue;
        }
        return userValue[0]
          ? convertUserToOption(userValue[0], false)
          : { label: "", value: "" };
      }

      case "toggle":
      case "checkbox":
        return Boolean(hasValue);

      case "link": {
        const parsed = LinkAttributeValueSchema.safeParse(value);
        if (parsed.success) {
          return parsed.data;
        }
        return { url: "", display_text: "" };
      }
      case "address": {
        return !col.is_not_applicable && value
          ? (value as AddressAttributeFormProps["savedValue"])
          : {};
      }
      default: {
        return (value as string) ?? "";
      }
    }
  })();

  const isAutofilled =
    has_reference_entity_mapping &&
    dependent_attributes_constraint?.action === "auto_fill";

  const isClearable = (col_id: string, isAutofilledSelect: boolean) =>
    has_reference_entity_mapping
      ? clearableAttributes?.has(col_id) || !isAutofilledSelect
      : true;

  const renderField = (): JSX.Element => {
    switch (col.input_type) {
      case "date":
        return (
          <DateWrapper>
            <DatePicker
              label={toTitleCase(
                col.display_name ? t([col.display_name]) : col.name
              )}
              name={col.id}
              isOutsideRange={() => false}
              defaultValue={defaultValue as string}
              required={col.is_required}
              methodsOfUseForm={{ ...methodsOfUseForm, errors }}
              normalPlaceholder={false}
              disabled={!col.is_editable || col.is_not_applicable}
              appendToBody={true}
            />
          </DateWrapper>
        );
      case "single_select":
        // Handle packaging type changes for generic SKU bulk packaging
        const isPackagingTypeField =
          col.object_type === "packaging_type" &&
          genericConfig?.enable_generic_sku &&
          genericConfig?.genericSkuPackagingTypeName &&
          genericConfig?.columns;

        const handlePackagingTypeChange = (
          selectedValue: OptionType<string> | null,
          onChange: (value: OptionType<string> | null) => void
        ) => {
          // Call the original onChange first
          onChange(selectedValue);

          // If bulk packaging is selected, reset toggle fields and clear packaging field errors
          if (
            selectedValue?.value ===
              genericConfig?.genericSkuPackagingTypeName &&
            genericConfig?.columns
          ) {
            genericConfig.columns.forEach((fieldCol) => {
              // Reset is_sample toggle field
              if (fieldCol.input_type === "toggle") {
                setValue(fieldCol.id, false);
              }
              // Clear validation errors for packaging fields
              if (
                fieldCol.object_type === "packaging_volume" ||
                fieldCol.object_type === "packaging_unit"
              ) {
                clearErrors(fieldCol.id);
              }
            });
          }
        };
        if (
          tenant_id &&
          shouldUseLazyListChoices(col, {
            hasReferenceEntityMapping: has_reference_entity_mapping,
            validValues: dependent_attributes_constraint?.valid_values,
          })
        ) {
          return (
            <CollectionSingleSelectListBackedField
              col={col}
              tenantId={tenant_id}
              defaultValue={defaultValue as OptionType<string>}
              methodsOfUseForm={methodsOfUseForm}
              errors={errors}
              t={t}
              is_single_select_disabled={is_single_select_disabled}
              menuPortalTarget={menuPortalTarget}
              menuPortalZIndex={menuPortalZIndex}
              dropdownZ={dropdownZ}
              isPackagingTypeField={!!isPackagingTypeField}
              onPackagingTypeChange={handlePackagingTypeChange}
            />
          );
        }
        return (
          <Controller
            control={control}
            name={col.id}
            rules={{
              required: col.is_required,
            }}
            defaultValue={defaultValue as OptionType<string>}
            render={({ onChange, value: formValue, name: fieldName }) => {
              let choices = col.choices ?? [];
              let filteredChoices = choices;
              let nextValue: { type: "clear" | "autofill"; value: any } | null =
                null;
              if (
                has_reference_entity_mapping &&
                dependent_attributes_constraint?.valid_values
              ) {
                choices = dependent_attributes_constraint.valid_values
                  .map((opt) => opt.display)
                  .sort((a, b) => a.localeCompare(b));
              }

              if (has_reference_entity_mapping) {
                const filteredResult = getFilteredChoices({
                  col: { ...col, choices: choices },
                  currentRow: row,
                  allRows,
                  allColumns,
                  watch,
                  collectionSchemaName,
                  constraint: dependent_attributes_constraint,
                  allDependentConstraints: all_dependent_constraints,
                  inputType: "single_select",
                });
                filteredChoices = filteredResult.filteredChoices;
                nextValue = filteredResult.nextValue;
              }

              const isAutofilledSelect =
                has_reference_entity_mapping &&
                dependent_attributes_constraint?.action === "auto_fill" &&
                !shouldSkipAutofillForReferenceEntityConstraint(
                  collectionSchemaName,
                  all_dependent_constraints,
                  allColumns,
                  {
                    attribute_id: col.id,
                    action: dependent_attributes_constraint.action,
                  },
                  filteredChoices.length
                );

              const options = filteredChoices.map((choice) => ({
                label: choice,
                value: choice,
              }));

              return (
                <>
                  <ApplyFilteredChoiceValue
                    colId={col.id}
                    inputType="single_select"
                    nextValue={nextValue}
                    setValue={setValue}
                    multiSelectRefs={multiSelectRefs}
                  />
                  <SelectBoxV2
                    key={fieldName || undefined}
                    name={fieldName}
                    placeholder={
                      col.display_name ? t([col.display_name]) : col.name
                    }
                    normalPlaceholder={menuPortalZIndex != null}
                    menuPortalTarget={menuPortalTarget}
                    isClearable={isClearable(col.id, isAutofilledSelect)}
                    isAutofilled={isAutofilledSelect}
                    options={options}
                    value={
                      formValue?.value
                        ? (formValue as OptionType<string>)
                        : null
                    }
                    onChange={(
                      selectedValue: OptionType<string> | null,
                      action: InputActionMeta
                    ) => {
                      if (isPackagingTypeField) {
                        handlePackagingTypeChange(selectedValue, onChange);
                      } else {
                        onChange(selectedValue);
                      }
                      if (has_reference_entity_mapping) {
                        updateDependentAttributes?.({
                          attributeId: col.id,
                          clearSelection:
                            (action.action as ActionTypes) === "clear",
                        });
                      }
                    }}
                    errors={errors}
                    formState={{ submitCount: 1 }}
                    disabled={
                      !col.is_editable ||
                      col.is_not_applicable ||
                      is_single_select_disabled
                    }
                    styles={
                      menuPortalTarget !== undefined
                        ? {
                            menuPortal: (base: any) => ({
                              ...base,
                              zIndex: dropdownZ,
                            }),
                          }
                        : undefined
                    }
                  />
                </>
              );
            }}
          />
        );
      case "product_reference": {
        return (
          <ProductReferenceField
            attribute={col}
            methodsOfUseForm={methodsOfUseForm}
            isDisabled={
              !col.is_editable ||
              col.is_not_applicable ||
              (has_reference_entity_mapping && isAutofilled)
            }
            isNotApplicable={col.is_not_applicable ?? false}
          />
        );
      }
      case "object_reference": {
        const objectReferenceValues = normalizeObjectReferenceValues(
          value,
          col.id,
          col.target_object_type
        );
        return (
          <ObjectReferenceField
            attribute={
              {
                ...col,
                values: objectReferenceValues,
              } as Extract<AttributeSchema, { input_type: "object_reference" }>
            }
            methodsOfUseForm={methodsOfUseForm}
            isDisabled={
              !col.is_editable ||
              col.is_not_applicable ||
              (has_reference_entity_mapping && isAutofilled)
            }
            isNotApplicable={col.is_not_applicable ?? false}
            excludeProfileId={excludeProfileId}
          />
        );
      }
      case "user": {
        return (
          <UserReferenceField
            attribute={col}
            methodsOfUseForm={methodsOfUseForm}
            isDisabled={
              !col.is_editable ||
              col.is_not_applicable ||
              (has_reference_entity_mapping && isAutofilled)
            }
            isNotApplicable={col.is_not_applicable ?? false}
            updateDependentAttributes={updateDependentAttributes}
            normalPlaceholder={menuPortalTarget !== undefined}
            menuPortalTarget={menuPortalTarget}
          />
        );
      }

      case "toggle":
        return (
          <Controller
            control={control}
            name={col.id}
            defaultValue={defaultValue as boolean}
            render={({ value: formValue, onChange }) => {
              return (
                <ToggleSwitchV2
                  label={toTitleCase(
                    col.display_name ? t([col.display_name]) : col.name
                  )}
                  name={col.id}
                  checked={
                    Array.isArray(formValue)
                      ? formValue.length > 0
                      : Boolean(formValue)
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onChange(e.target.checked)
                  }
                  disabled={
                    !col.is_editable ||
                    col.is_not_applicable ||
                    (col.object_type === "is_a_sample" &&
                      currentPackagingType === genericSkuPackagingTypeName)
                  }
                />
              );
            }}
          />
        );
      case "checkbox":
        return (
          <Controller
            control={control}
            name={col.id}
            defaultValue={defaultValue as boolean}
            render={({ value: formValue, onChange }) => {
              return (
                <ToggleSwitchV2
                  label={""}
                  name={col.id}
                  checked={
                    Array.isArray(formValue)
                      ? formValue.length > 0
                      : Boolean(formValue)
                  }
                  onChange={(e: ChangeEvent<HTMLInputElement>) =>
                    onChange(e.target.checked)
                  }
                  disabled={!col.is_editable || col.is_not_applicable}
                />
              );
            }}
          />
        );
      case "numeric":
        return (
          <TextField
            name={col.id}
            label={`${toTitleCase(
              col.display_name ? t([col.display_name]) : col.name
            )} (number)`}
            theref={register()}
            defaultValue={defaultValue as string | number}
            formState={formState}
            errors={errors}
            type="number"
            readOnly={!col.is_editable || col.is_not_applicable}
          />
        );
      case "multiline_entry":
        return (
          <RichEditor
            value={defaultValue as string}
            id={`${col.id}${row.uuid}`}
            name={`${col.id}`}
            label={toTitleCase(
              col.display_name ? t([col.display_name]) : col.name
            )}
            useFormMethods={{ register, formState, setValue, errors, watch }}
            readOnly={!col.is_editable || col.is_not_applicable}
          />
        );
      case "multi_select": {
        if (
          tenant_id &&
          shouldUseLazyListChoices(col, {
            hasReferenceEntityMapping: has_reference_entity_mapping,
            validValues: dependent_attributes_constraint?.valid_values,
          })
        ) {
          return (
            <Controller
              name={col.id}
              control={control}
              defaultValue={defaultValue}
              render={() => (
                <MultiSelectField
                  type="collection"
                  attribute={col}
                  methodsOfUseForm={{ ...methodsOfUseForm, errors }}
                  isDisabled={!col.is_editable || col.is_not_applicable}
                  isAutofilled={false}
                  isNotApplicable={col?.is_not_applicable ?? false}
                  multiSelectRefs={multiSelectRefs}
                  updateDependentAttributes={updateDependentAttributes}
                  menuPortalTarget={menuPortalTarget}
                />
              )}
            />
          );
        }

        let choices = col.choices ?? [];
        let multiSelectFilteredChoices = choices;
        let nextValue: { type: "clear" | "autofill"; value: any } | null = null;
        if (
          has_reference_entity_mapping &&
          dependent_attributes_constraint?.valid_values
        ) {
          choices = dependent_attributes_constraint.valid_values.map(
            (opt) => opt.display
          );
        }

        if (has_reference_entity_mapping) {
          const filteredResult = getFilteredChoices({
            col: { ...col, choices: choices },
            currentRow: row,
            allRows,
            allColumns,
            watch,
            collectionSchemaName,
            constraint: dependent_attributes_constraint,
            allDependentConstraints: all_dependent_constraints,
            inputType: "multi_select",
          });
          multiSelectFilteredChoices = filteredResult.filteredChoices;
          nextValue = filteredResult.nextValue;
        }

        const isAutofilledMulti =
          has_reference_entity_mapping &&
          dependent_attributes_constraint?.action === "auto_fill" &&
          !shouldSkipAutofillForReferenceEntityConstraint(
            collectionSchemaName,
            all_dependent_constraints,
            allColumns,
            {
              attribute_id: col.id,
              action: dependent_attributes_constraint.action,
            },
            multiSelectFilteredChoices.length
          );

        return (
          <>
            <ApplyFilteredChoiceValue
              colId={col.id}
              inputType="multi_select"
              nextValue={nextValue}
              setValue={setValue}
              multiSelectRefs={multiSelectRefs}
            />
            <Controller
              name={col.id}
              control={control}
              defaultValue={defaultValue}
              render={() => {
                return (
                  <MultiSelectField
                    type="collection"
                    attribute={{
                      ...col,
                      choices: multiSelectFilteredChoices,
                    }}
                    methodsOfUseForm={{ ...methodsOfUseForm, errors }}
                    isDisabled={!col.is_editable || col.is_not_applicable}
                    isAutofilled={isAutofilledMulti}
                    isNotApplicable={col?.is_not_applicable ?? false}
                    multiSelectRefs={multiSelectRefs}
                    {...(has_reference_entity_mapping && {
                      dependent_choices: multiSelectFilteredChoices.map(
                        (option) => ({
                          label: option,
                          value: option,
                        })
                      ),
                    })}
                    updateDependentAttributes={updateDependentAttributes}
                    menuPortalTarget={menuPortalTarget}
                  />
                );
              }}
            />
          </>
        );
      }
      case "link": {
        const linkDefaultValue = defaultValue as {
          url: string;
          display_text: string;
        };

        return (
          <LinkAttributeField
            name={col.id}
            label={col.display_name ? t([col.display_name]) : col.name}
            control={control}
            register={register}
            formState={formState}
            errors={errors}
            defaultValue={linkDefaultValue}
            isRequired={col.is_required}
            isDisabled={!col.is_editable}
          />
        );
      }
      case "address":
        return (
          <FormProvider {...methodsOfUseForm}>
            <AddressAttributeForm
              fieldPrefix={col.id}
              attributeName={col.display_name ?? col.name}
              mode={col.address_mode ?? "standard"}
              savedValue={
                defaultValue as AddressAttributeFormProps["savedValue"]
              }
              isNotApplicable={col.is_not_applicable ?? false}
              disabled={!col.is_editable || col.is_not_applicable}
            />
          </FormProvider>
        );
      case "document": {
        const docValues = (value as DocumentCollectionValue[] | null) ?? [];
        const colWithDocValues = {
          ...col,
          values: docValues.map((docVal, idx) => {
            const attr_val = col.default_values?.find(
              (df) =>
                (df.value as DocumentAttributeValue)?.asset_id ===
                docVal.asset_id
            );
            if (attr_val) {
              const { value, ...rest } = attr_val;
              return { ...rest, value: docVal } as AttributeValue;
            }
            return {
              id: docVal.asset_id as UUID,
              priority: idx,
              enum_variant: null,
              attribute_id: col.id as UUID,
              value: {
                asset_id: docVal.asset_id,
                display_name: docVal.display_name,
                is_external: docVal.is_external,
              } as DocumentAttributeValue,
              data_type: "document" as const,
              cdn_url: docVal.url,
            };
          }),
        };
        return (
          <DocumentAttribute
            attribute={colWithDocValues}
            methodsOfUseForm={methodsOfUseForm}
            isDisabled={!col.is_editable || col.is_not_applicable}
            isNotApplicable={col.is_not_applicable ?? false}
          />
        );
      }
      default:
        return (
          <TextField
            name={col.id}
            theref={register()}
            formState={formState}
            errors={errors}
            label={toTitleCase(
              col.display_name ? t([col.display_name]) : col.name
            )}
            defaultValue={defaultValue as string}
            type="text"
            form={row.uuid}
            readOnly={
              col.object_type === "sku_id" ||
              !col.is_editable ||
              col.is_not_applicable
            }
          />
        );
    }
  };

  const fieldElement: JSX.Element = renderField();

  const fieldWithEnforce =
    shouldEnforceLockedSkuStatus && lockSkuStatusValue ? (
      <>
        <EnforceLockedSkuStatusValue
          shouldEnforce={shouldEnforceLockedSkuStatus}
          colId={col.id}
          enforcedValue={lockSkuStatusValue}
          methodsOfUseForm={methodsOfUseForm}
        />
        {fieldElement}
      </>
    ) : (
      fieldElement
    );

  if (
    (col.input_type === "product_reference" ||
      col.input_type === "object_reference") &&
    !(Array.isArray(value) && (value as any[])?.length > 1)
  ) {
    return (
      <FormFieldInitializer
        col={col}
        value={value}
        row={row}
        methodsOfUseForm={methodsOfUseForm}
      >
        {fieldWithEnforce}
      </FormFieldInitializer>
    );
  }

  return fieldWithEnforce;
};

export const ActiveCheckIcon = () => {
  const theme = useTheme();
  return <CheckIcon fill={theme.activeToggleBG} />;
};

export const DestructiveXIcon = () => {
  const theme = useTheme();
  return <XIcon fill={theme.destructiveTextColor} />;
};

export const generateUUID = <T extends { uuid: string }>(arr: T[]) => {
  const lastUUID = arr[arr.length - 1]?.uuid;
  return !lastUUID
    ? `${Math.floor(Math.random() * 1234)}${GENERATED_UUID}`
    : lastUUID.includes(GENERATED_UUID)
    ? `${lastUUID}-${Math.floor(Math.random() * 1234)}`
    : `${lastUUID}${GENERATED_UUID}-${Math.floor(Math.random() * 1234)}`;
};

export const GenerateForm = ({
  rowInEdit,
  methodForm,
  onSubmitForm,
  errorRef,
}: {
  rowInEdit: UUID;
  methodForm: UseFormMethods<FormValue>;
  onSubmitForm: (formValue: OnSubmitValue) => Promise<void>;
  errorRef: MutableRefObject<DeepMap<FormValue, FieldError> | null>;
}) => {
  const { notifyError } = useNotifications();
  const { t } = useTranslation();
  return (
    <form
      noValidate
      onSubmit={methodForm.handleSubmit(
        (values: FormValue) => {
          const modifiedValues: FormValue = {};
          Object.entries(values).forEach(([key, value]) => {
            if (Array.isArray(value)) {
              modifiedValues[key] =
                value.length === 0
                  ? ""
                  : (value as Array<unknown>).map((val: any) => {
                      // Support both ChipType ({ name }) and OptionType ({ label, value })
                      if (val && typeof val === "object") {
                        if ("name" in val) {
                          return (val as ChipType).name;
                        }
                        if ("value" in val) {
                          return val.value;
                        }
                      }
                      return val as any;
                    });
            } else {
              modifiedValues[key] =
                value instanceof Date ? value.toISOString() : value;
            }
          });
          onSubmitForm({
            ...modifiedValues,
            row_id: rowInEdit,
          });
        },
        (errors: DeepMap<FormValue, FieldError>) => {
          errorRef.current = errors;
          if (errors["select_a_field"]) {
            notifyError(t("Enter at least one field."));
          } else {
            const errorKeys = Object.keys(errors);
            if (errorKeys.length > 0) {
              console.error("Validation failed for fields:", errorKeys);
              const firstError = errors[errorKeys[0]];
              if (firstError?.message) {
                notifyError(firstError.message);
              }
            }
          }
        }
      )}
      id={rowInEdit}
      style={{ display: "none" }}
    />
  );
};

export const editCollectionRow = async ({
  row_id,
  values,
  values_to_delete,
  tenant_id,
  product_id,
  collection_id,
}: {
  row_id: string;
  values: {
    attribute_id: string;
    value: string | { display_text: string; url: string } | null;
  }[];
  values_to_delete: string[];
  tenant_id: string;
  product_id: string;
  collection_id: string;
}) => {
  const response = await Axios.patch<
    {
      values: {
        attribute_id: string;
        value: string | { display_text: string; url: string };
      }[];
    },
    AxiosResponse<PIMProductPartial>
  >(
    endpoints.v2_tenants_id_or_slug_pim_products_id_collections_id_rows_id(
      tenant_id,
      product_id,
      collection_id,
      row_id
    ),
    {
      values,
      values_to_delete,
    }
  );
  return response;
};

export const deleteRow = async ({
  tenant_id,
  product_id,
  collection_id,
  row_id,
}: {
  tenant_id: string;
  product_id: string;
  collection_id: string;
  row_id: string;
}) => {
  await Axios.delete(
    endpoints.v2_tenants_id_or_slug_pim_products_id_collections_id_rows_id(
      tenant_id,
      product_id,
      collection_id,
      row_id
    )
  );
};

export const getAttributeFormValue = (
  value:
    | string
    | boolean
    | OptionType<string | boolean>
    | { display_text: string; url: string }
    | undefined
) => {
  const parsedLink = LinkAttributeValueSchema.safeParse(value);
  const parsedOptionType = z
    .object({
      value: z.union([z.string(), z.boolean()]),
      label: z.string(),
    })
    .safeParse(value);
  if (parsedLink.success) {
    const linkData = parsedLink.data;
    if (!linkData.display_text && !linkData.url) {
      return null;
    }
    return linkData;
  }

  if (parsedOptionType.success) {
    const value = parsedOptionType.data.value;
    return value === "" ? null : value;
  }
  if (value === undefined || value === null || value === "") {
    return null;
  }
  return value as any;
};

export const addRow = async ({
  values,
  tenant_id,
  product_id,
  collection_id,
}: {
  values: {
    attribute_id: string;
    value: string | { display_text: string; url: string } | null;
  }[];
  tenant_id: string;
  product_id: string;
  collection_id: string;
}) => {
  const response = await Axios.patch<
    {
      rows: {
        values: {
          attribute_id: string;
          value: string | { display_text: string; url: string };
        }[];
      }[];
    },
    AxiosResponse<PIMProductPartial>
  >(
    endpoints.v2_tenants_id_or_slug_pim_products_id_collections_id(
      tenant_id,
      product_id,
      collection_id
    ),
    {
      rows: [
        {
          values,
        },
      ],
    }
  );
  return response;
};
